/**
 * Uproszczony moduł zarządzający stanem początkowym produktów
 */

// Stała klucz do przechowywania początkowego stanu w localStorage
const INITIAL_STATE_KEY = 'initialProductState';
// Dodatkowy klucz na automatyczne migawki systemowe 
const TEMP_SNAPSHOT_KEY = 'tempSystemSnapshot';

/**
 * Zapisuje początkowy stan produktów po wczytaniu pliku XML
 * @param {Array} products - Lista produktów z pliku XML
 * @param {string} fileName - Nazwa pliku XML
 * @param {string} [mode] - Tryb zapisu: undefined (domyślny) = stan początkowy, 'system' = migawka systemowa
 * @returns {boolean} - Czy operacja się powiodła
 */
export function saveCurrentPrioritiesSnapshot(products, fileName, mode) {
  try {
    // Przy migawkach systemowych używamy innego klucza, aby nie nadpisać oryginalnego stanu początkowego
    const storageKey = mode === 'system' ? TEMP_SNAPSHOT_KEY : INITIAL_STATE_KEY;
    
    console.log(`Zapisywanie ${mode === 'system' ? 'migawki systemowej' : 'początkowego stanu'} produktów...`);
    
    // Format daty: dd/mm hh:mm
    const timestamp = new Date().toLocaleString('pl-PL', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
    
    // Wykorzystajmy tę samą optymalizację dla obu typów zapisów
    // Ograniczamy ilość danych, zachowując tylko niezbędne informacje
    const minimalProductData = products.map(product => {
      // Podstawowe informacje o produkcie
      const minimalProduct = {
        id: product.id,
        name: product.name,
        codeOnCard: product.codeOnCard,
        iconUrl: product.iconUrl, // Zachowujemy URL ikony dla identyfikacji wizualnej
        menuItems: product.menuItems.map(item => ({
          textId: item.textId,
          level: item.level
        }))
      };
      
      // Zapisujemy niezbędne informacje z originalData jeśli są dostępne
      if (product.originalData) {
        minimalProduct.originalData = {
          id: product.originalData.id
        };
        
        // Zapisujemy priority_menu jeśli istnieje
        if (product.originalData['iaiext:priority_menu']) {
          minimalProduct.originalData['iaiext:priority_menu'] = 
            product.originalData['iaiext:priority_menu'];
        }
      }
      
      return minimalProduct;
    });
    
    const initialState = {
      createdAt: new Date().toISOString(),
      timestamp,
      fileName,
      products: minimalProductData
    };
    
    // Zapisujemy zoptymalizowany stan w localStorage
    localStorage.setItem(storageKey, JSON.stringify(initialState));
    console.log(`Pomyślnie zapisano zoptymalizowany ${mode === 'system' ? 'migawkę systemową' : 'stan początkowy'}`);
    
    return true;
  } catch (error) {
    console.error(`Błąd podczas zapisywania ${mode === 'system' ? 'migawki systemowej' : 'początkowego stanu'}:`, error);
    return false;
  }
}

/**
 * Przywraca początkowy stan produktów po wczytaniu pliku XML
 * @param {function} onRestore - Funkcja wywoływana po przywróceniu stanu
 * @returns {Object} - Informacja o wyniku operacji
 */
export function restoreInitialState(onRestore) {
  try {
    console.log('=== PRZYWRACANIE POCZĄTKOWEGO STANU PRODUKTÓW - START ===');
    
    // Pobieramy początkowy stan z localStorage
    const initialStateJson = localStorage.getItem(INITIAL_STATE_KEY);
    
    if (!initialStateJson) {
      console.error('Brak zapisanego stanu początkowego');
      return {
        success: false,
        message: 'Brak zapisanego stanu początkowego'
      };
    }
    
    console.log('Znaleziono zapisany stan początkowy, długość danych:', initialStateJson.length);
    
    // Parsujemy zapisany stan
    const initialState = JSON.parse(initialStateJson);
    console.log('Pomyślnie sparsowano dane JSON');
    
    // Sprawdzamy czy mamy produkty
    if (!initialState.products || !Array.isArray(initialState.products) || initialState.products.length === 0) {
      console.error('Zapisany stan początkowy nie zawiera produktów');
      return {
        success: false,
        message: 'Zapisany stan początkowy nie zawiera produktów'
      };
    }
    
    console.log(`Znaleziono ${initialState.products.length} produktów w stanie początkowym z pliku ${initialState.fileName || 'nieznanym'}`);
    
    // Sprawdzamy strukturę pierwszego produktu
    if (initialState.products.length > 0) {
      const firstProduct = initialState.products[0];
      console.log('Struktura przykładowego produktu:');
      console.log('- ID:', firstProduct.id);
      console.log('- Nazwa:', firstProduct.name);
      console.log('- Liczba menuItems:', firstProduct.menuItems ? firstProduct.menuItems.length : 0);
      console.log('- Posiada originalData:', !!firstProduct.originalData);
      console.log('- Posiada iconUrl:', !!firstProduct.iconUrl);
      
      // Sprawdzamy czy produkt ma wszystkie niezbędne pola
      if (!firstProduct.id || !firstProduct.menuItems || !Array.isArray(firstProduct.menuItems)) {
        console.error('Nieprawidłowa struktura produktu - brak wymaganych pól');
        return {
          success: false,
          message: 'Nieprawidłowa struktura produktu w zapisanym stanie'
        };
      }
    }
    
    // Wywołujemy funkcję zwrotną z przywróconymi produktami
    if (onRestore && typeof onRestore === 'function') {
      console.log('Wywołanie funkcji zwrotnej z przywróconymi produktami');
      onRestore(initialState.products);
    } else {
      console.error('Nie podano funkcji zwrotnej do obsługi przywróconych produktów');
    }
    
    console.log('=== PRZYWRACANIE POCZĄTKOWEGO STANU PRODUKTÓW - ZAKOŃCZONE ===');
    
    return {
      success: true,
      message: 'Przywrócono początkowy stan produktów',
      productsCount: initialState.products.length,
      fileName: initialState.fileName,
      timestamp: initialState.timestamp
    };
  } catch (error) {
    console.error('Błąd podczas przywracania początkowego stanu:', error);
    return {
      success: false,
      message: `Błąd podczas przywracania: ${error.message}`
    };
  }
}

/**
 * Sprawdza, czy istnieje zapisany początkowy stan
 * @returns {boolean} - Czy istnieje zapisany stan początkowy
 */
export function hasInitialState() {
  const initialStateJson = localStorage.getItem(INITIAL_STATE_KEY);
  return !!initialStateJson;
}

/**
 * Pobiera informacje o zapisanym stanie początkowym
 * @returns {Object|null} - Informacje o stanie początkowym lub null jeśli brak
 */
export function getInitialStateInfo() {
  try {
    const initialStateJson = localStorage.getItem(INITIAL_STATE_KEY);
    
    if (!initialStateJson) {
      return null;
    }
    
    const initialState = JSON.parse(initialStateJson);
    
    return {
      timestamp: initialState.timestamp || new Date(initialState.createdAt).toLocaleString(),
      fileName: initialState.fileName || 'plik XML',
      productsCount: initialState.products ? initialState.products.length : 0
    };
  } catch (error) {
    console.error('Błąd podczas pobierania informacji o stanie początkowym:', error);
    return null;
  }
} 